"use client"

import React from 'react';
import { useState, useEffect, useMemo } from "react"
import api from "../api"
import { useNavigate } from "react-router-dom"
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async"
import EventGrid from "../components/EventGrid"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import { useAuth } from "../context/AuthContext"
import { motion } from "framer-motion"
import "./Home.css"

// Relative "x ago" label from a timestamp.
const timeAgo = (ts) => {
  if (!ts) return "recently"
  const d = new Date(String(ts).replace(" ", "T"))
  const secs = Math.floor((Date.now() - d.getTime()) / 1000)
  if (isNaN(secs)) return "recently"
  if (secs < 60) return "just now"
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

const Home = () => {
  // Initialize state
  const [visibleEvents, setVisibleEvents] = useState(9)
  const [loading, setLoading] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [events, setEvents] = useState([])
  const [invitations, setInvitations] = useState([])
  const [guests, setGuests] = useState([])
  const [error, setError] = useState(null)

  // Dependencies
  const navigate = useNavigate()
  const { showSuccessModal, showFailureModal } = useSuccessFailure()
  const { user, loading: authLoading } = useAuth()

  // Environment variables
  const faviconUrl = process.env.REACT_APP_FAVICON_URL || "/eventsawa.jpg"
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"

  // Combined effect for loading and data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Show loading state
        setLoading(true);
        setIsLoaded(false);
        setError(null); // Clear any previous errors

        // First fetch public events with pagination
        // console.log('Fetching events from API...');
        const eventsResponse = await api.get('/events/all/');
        // console.log('API Response:', eventsResponse);

        // Handle the response structure - check if data is an array or an object with events property
        let eventsData = [];
        if (Array.isArray(eventsResponse.data)) {
          // If the response is an array, use it directly
          eventsData = eventsResponse.data;
        } else if (eventsResponse.data && Array.isArray(eventsResponse.data.events)) {
          // If the response has an events array, use that
          eventsData = eventsResponse.data.events;

          // Store pagination info if available
          if (eventsResponse.data.total_events) {
            // console.log('Total events available:', eventsResponse.data.total_events);
          }
        } else {
          // console.warn('Unexpected API response format:', eventsResponse.data);
        }

        // console.log('Processed events data:', eventsData);

        // Filter events based on visibility and user permissions
        // Note: The backend should handle most of the filtering, but we'll keep this as a safeguard
        const filteredEvents = eventsData.filter(event => {
          if (!event) return false;

          // Make sure the event has required properties
          if (!event.visibility) {
            // console.warn('Event missing visibility:', event);
            return false;
          }

          // For non-authenticated users, only show public events
          if (!user) {
            return event.visibility === 'public';
          }

          // For authenticated users, show public events, their own events, or events they're invited to
          return event.visibility === 'public' ||
            (event.created_by && (typeof event.created_by === 'object' ? event.created_by.id === user.id : event.created_by === user.id)) ||
            (event.guests && Array.isArray(event.guests) && event.guests.some(guest =>
              guest && (typeof guest === 'object' ? guest.guest === user.id : guest === user.id)
            ));
        });

        // console.log('Filtered events:', filteredEvents);
        setEvents(filteredEvents);

        // If user is logged in, fetch private data
        if (user) {
          try {
            const response = await api.get('/invitations/bulk/');
            setInvitations(response.data.invitations || []);
            setGuests(response.data.guests || []);
          } catch (err) {
            // console.error("Error fetching invitations:", err);
            setError("Failed to fetch invitations and guests");
          }
        }

        // Set loading states after successful fetch
        setLoading(false);
        setIsLoaded(true);
      } catch (err) {
        // console.error("Error fetching events:", err);
        setError(err.response?.data?.error || "Failed to fetch events");
        setLoading(false);
        setIsLoaded(true);
      }
    };

    // Clear existing data before fetching new data
    setEvents([]);
    setInvitations([]);
    setGuests([]);

    fetchData();

    // Cleanup function
    return () => {
      setLoading(false);
      setIsLoaded(true);
      setError(null);
    };
  }, [user]);

  // Fetch invitations and guests for the current event
  const fetchBulkInvitationsAndGuests = async () => {
    if (!user) {
      return
    }

    try {
      const response = await api.get('/invitations/bulk/');
      const data = response.data
      // console.log("Bulk invitation and guest data:", data)

      if (data) {
        setInvitations(data.invitations || [])
        setGuests(data.guests || [])
      } else {
        showFailureModal("Failed to fetch invitations and guests.")
      }
    } catch (error) {
      // console.error("Error fetching invitations and guests:", error)
      showFailureModal("Error fetching invitations and guests.")
    }
  }

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get('/events/all/')
        // Extract events from the response
        const eventsData = response.data.events || response.data || []
        setEvents(eventsData)
      } catch (error) {
        // console.error("Error fetching events:", error)
        setError(error.response?.data?.error || "Failed to fetch events")
      } finally {
        setLoading(false)
        setIsLoaded(true)
      }
    }

    fetchEvents()
  }, [])

  // Fetch invitations and guests after events are loaded
  useEffect(() => {
    if (user && events.length > 0) {
      fetchBulkInvitationsAndGuests()
    }
  }, [events, user])

  // Memoized filtered events
  const filteredEvents = useMemo(() => {
    if (!events || !events.length) return [];

    return events.filter(event => {
      const searchMatch = !searchQuery ||
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.location.toLowerCase().includes(searchQuery.toLowerCase())

      const isPublic = event.visibility === "public"
      const isCreator = user?.id === event.created_by
      const isInvited = user
        ? invitations.some(invitation => invitation.event.id === event.id)
        : false

      // Always show public events
      if (isPublic) return true;

      // For logged in users, show their own events and invited events
      if (user) return isCreator || isInvited;

      // For non-logged in users, only show public events
      return false;
    })
  }, [events, searchQuery, user, invitations])

  // Load 9 more events
  const handleShowMore = () => {
    setVisibleEvents(visibleEvents + 9) // Increase the number of visible events by 9
  }

  // Handle event click to navigate to the event details page
  const handleEventClick = (eventSlug) => {
    navigate(`/events/${eventSlug}`) // Navigate to a specific event's page
  }

  // Show loading indicator
  if (authLoading || loading) {
    return <p className="text-gray-500 dark:text-gray-400">Loading...</p>
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{eventTitle} - Home</title>
        <meta name="description" content={eventDescription} />
        <link rel="icon" href={faviconUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://eventsawa.goelway.com" />
        <meta property="og:title" content={eventTitle} />
        <meta property="og:description" content={eventDescription} />
        <meta property="og:image" content="/eventsawa.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://eventsawa.goelway.com" />
        <meta name="twitter:title" content={eventTitle} />
        <meta name="twitter:description" content={eventDescription} />
        <meta name="twitter:image" content="/eventsawa.jpg" />
      </Helmet>

      {/* Enhanced Hero Section - More Compact & Beautiful */}
      <section className="relative overflow-hidden bg-white dark:bg-gray-900 -mt-24 pt-24">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-indigo-950"></div>

          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-indigo-200 to-purple-200 dark:from-indigo-900 dark:to-purple-900 rounded-bl-[100px] opacity-20 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-to-tr from-blue-200 to-indigo-200 dark:from-blue-900 dark:to-indigo-900 rounded-tr-[100px] opacity-20 blur-3xl"></div>

          {/* Subtle Pattern */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.03] dark:opacity-[0.02]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M0 0 L40 0 L40 40 L0 40 Z" fill="none" stroke="currentColor" strokeWidth="0.5"></path>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)"></rect>
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center py-12 lg:py-16">
            {/* Content Column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="space-y-6"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800"
              >
                <span className="w-2 h-2 rounded-full bg-indigo-500 mr-2"></span>
                <span className="text-xs font-medium text-indigo-800 dark:text-indigo-200">
                  Reimagining Event Management
                </span>
              </motion.div>

              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight"
                >
                  <span className="block text-gray-900 dark:text-white">We make events</span>
                  <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    sawa.
                  </span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-lg"
                >
                  From personal to corporate, we are the go-to digital platform where stakeholders plan, actualize and measure how well the event went.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <Link
                  to="/login"
                  className="relative inline-flex items-center justify-center px-5 py-3 text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 group"
                >
                  <span className="relative z-10 flex items-center">
                    Get started
                    <svg
                      className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </span>
                  <span className="absolute -inset-0.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur"></span>
                </Link>

                <a
                  href="mailto:eventsawa@goelway.com"
                  className="inline-flex items-center justify-center px-5 py-3 text-base font-medium text-indigo-700 dark:text-indigo-300 bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-800 rounded-lg shadow-sm hover:bg-indigo-50 dark:hover:bg-gray-700 transform transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <svg
                    className="w-5 h-5 mr-2 text-indigo-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    ></path>
                  </svg>
                  Speak to Sales
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="flex items-center space-x-4 pt-2"
              >
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <img
                      key={i}
                      className="inline-block h-8 w-8 rounded-full ring-2 ring-white dark:ring-gray-800"
                      src={`https://ui-avatars.com/api/?name=User+${i}&background=${['#6366f1', '#2563eb', '#16a34a', '#db2777'][i - 1]}&color=fff&size=32&bold=true`}
                      alt={`User ${i}`}
                    />
                  ))}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-indigo-600 dark:text-indigo-400">1000s+</span> of event planners trust
                  us
                </div>
              </motion.div>
            </motion.div>

            {/* Image Column */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: isLoaded ? 1 : 0, x: isLoaded ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="relative"
            >
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl transform transition-transform duration-500 hover:scale-[1.02]">
                {/* Main image with device frame */}
                <div className="relative bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 p-4 rounded-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80"
                    alt="A vibrant celebration with a cheering crowd and confetti"
                    loading="eager"
                    className="w-full relative z-10 rounded-xl object-cover aspect-[4/3]"
                  />

                  {/* Subtle reflection effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent mix-blend-overlay rounded-2xl"></div>
                </div>

                {/* Floating notification cards */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="absolute -left-6 top-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 z-20 max-w-[160px]"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate max-w-[110px]">
                        {events[0]?.title || "New event"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{timeAgo(events[0]?.created_at)}</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className="absolute -right-6 bottom-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 z-20 max-w-[160px]"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900 dark:text-white">
                        {events.reduce((s, e) => s + (e.guest_count || 0), 0)} RSVPs
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">across {events.length} events</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 60"
            className="w-full h-auto"
            preserveAspectRatio="none"
          >
            <path
              fill="currentColor"
              fillOpacity="1"
              className="text-gray-50 dark:text-gray-900"
              d="M0,32L60,37.3C120,43,240,53,360,53.3C480,53,600,43,720,37.3C840,32,960,32,1080,37.3C1200,43,1320,53,1380,58.7L1440,64L1440,64L1380,64C1320,64,1200,64,1080,64C960,64,840,64,720,64C600,64,480,64,360,64C240,64,120,64,60,64L0,64Z"
            ></path>
          </svg>
        </div>
      </section>


      {/* Events Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <EventGrid
          events={events}
          invitations={invitations}
          user={user}
          visibleEvents={visibleEvents}
          onEventClick={handleEventClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onShowMore={handleShowMore}
        />
      </div>

      {/* Features Section */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Everything you need for successful events
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Our platform provides all the tools you need to create, manage, and measure the success of your events.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    ></path>
                  </svg>
                ),
                title: "Event Creation",
                description:
                  "Create beautiful event pages with customizable templates, ticketing options, and RSVP management.",
              },
              {
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    ></path>
                  </svg>
                ),
                title: "Guest Management",
                description:
                  "Manage invitations, track RSVPs, assign seating, and communicate with attendees in real-time.",
              },
              {
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    ></path>
                  </svg>
                ),
                title: "Analytics & Insights",
                description:
                  "Get detailed analytics on attendance, engagement, and feedback to measure the success of your events.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Footer Section */}
      <footer className="bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <a href="/" className="flex items-center mb-6">
                <img src={`${process.env.PUBLIC_URL}/eventsawa.jpg`} className="mr-3 h-9 w-9 rounded-full object-cover" alt="EventSawa Logo" />
                <div className="flex flex-col">
                  <span className="text-xl font-bold font-sans whitespace-nowrap text-gray-900 dark:text-white">
                    {eventTitle}
                  </span>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {eventDescription}
                  </span>
                </div>
              </a>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                The premiere platform for event planning, management, and execution. Create memorable experiences with
                ease.
              </p>
              <div className="flex space-x-4">
                {["facebook", "twitter", "instagram", "linkedin"].map((social) => (
                  <a
                    key={social}
                    href="#"
                    className="text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    <span className="sr-only">{social}</span>
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      {social === "facebook" && (
                        <path
                          fillRule="evenodd"
                          d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                          clipRule="evenodd"
                        />
                      )}
                      {social === "twitter" && (
                        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                      )}
                      {social === "instagram" && (
                        <path
                          fillRule="evenodd"
                          d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                          clipRule="evenodd"
                        />
                      )}
                      {social === "linkedin" && (
                        <path
                          fillRule="evenodd"
                          d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                          clipRule="evenodd"
                        />
                      )}
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:col-span-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4">Resources</h3>
                <ul className="space-y-3">
                  {/* {["Documentation", "Tutorials", "Support", "API Reference", "Blog"].map((item) => ( */}
                  {["Documentation"].map((item) => (
                    <li key={item}>
                      <Link
                        to={item === 'Documentation' ? '/documentation' : '#'}
                        className="text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors"
                      >
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4">Company</h3>
                <ul className="space-y-3">
                  {/* {["About Us", "Careers", "Partners", "Contact Us", "Press"].map((item) => ( */}
                  {["About Us"].map((item) => (
                    <li key={item}>
                      <Link
                        to={item === 'About Us' ? '/about' : '#'}
                        className="text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors"
                      >
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4">Legal</h3>
                <ul className="space-y-3">
                  {["Privacy Policy", "Terms of Service"].map((item) => (
                    <li key={item}>
                      <Link
                        to={item === 'Privacy Policy' ? '/privacy-policy' : '/terms-of-service'}
                        className="text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors"
                      >
                        {item}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                © {new Date().getFullYear()} {eventTitle}. All rights reserved.
              </p>
              <div className="mt-4 md:mt-0">
                <select className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block py-2.5 pl-3 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500">
                  <option value="en">English (US)</option>
                  {/* <option value="fr">Français</option>
                  <option value="es">Español</option>
                  <option value="de">Deutsch</option> */}
                </select>
              </div>
            </div>
          </div>
        </div>
      </footer>

    </div>
  )
}

export default Home